/**
 * Scheduling helpers for the delivery board.
 *
 * `scheduledFor` is a local ISO string — `YYYY-MM-DDTHH:MM`, no timezone. A
 * delivery is scheduled for 08:20 *at the site*, not at an instant in UTC, and
 * keeping it as one string means the date and time halves can never drift
 * apart. It also sorts chronologically as a plain string compare.
 */

const SCHEDULE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/

export function isValidSchedule(scheduledFor: string): boolean {
  return SCHEDULE_PATTERN.test(scheduledFor.trim())
}

/** `YYYY-MM-DD` — the grouping key for a day section. */
export function toDayKey(scheduledFor: string): string {
  return scheduledFor.slice(0, 10)
}

/** `HH:MM` — what the tile rail displays. */
export function toClockTime(scheduledFor: string): string {
  return scheduledFor.slice(11, 16)
}

/** `YYYY-MM-DD` for a Date, in local time (not UTC — see the module note). */
export function dayKeyOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const LAST_MINUTE_OF_DAY = 24 * 60 - 1

/** Minutes since midnight for an `HH:MM` clock time. */
export function clockToMinutes(clock: string): number {
  return Number(clock.slice(0, 2)) * 60 + Number(clock.slice(3, 5))
}

function minutesToClock(minutes: number): string {
  const clamped = Math.min(Math.max(Math.round(minutes), 0), LAST_MINUTE_OF_DAY)
  const hours = Math.floor(clamped / 60)
  return `${String(hours).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`
}

/** Same day, different clock time. */
export function withClockTime(scheduledFor: string, clock: string): string {
  return `${toDayKey(scheduledFor)}T${clock}`
}

/**
 * Delay, staying on the delivery's own day.
 *
 * Clamps at 23:59 rather than rolling into tomorrow: deliveries run roughly
 * 07:00–19:00, so a delay that would cross midnight is almost certainly not
 * what the tap meant. Genuinely moving a delivery to another day is a
 * View / Edit job on the whole record.
 *
 * Relative to the delivery's *current* time, so a second call accumulates —
 * "+20 late" then "+15 later still" lands at +35.
 */
export function delayWithinDay(scheduledFor: string, minutes: number): string {
  if (!isValidSchedule(scheduledFor)) return scheduledFor
  const shifted = clockToMinutes(toClockTime(scheduledFor)) + minutes
  return withClockTime(scheduledFor, minutesToClock(shifted))
}

/** True when a delay would hit the clamp instead of moving the full amount. */
export function wouldClamp(scheduledFor: string, minutes: number): boolean {
  if (!isValidSchedule(scheduledFor)) return false
  return (
    clockToMinutes(toClockTime(scheduledFor)) + minutes > LAST_MINUTE_OF_DAY
  )
}

/**
 * Right now, to the minute. `clock` is injectable for tests.
 *
 * Backs the arrival action ("Just arrived" / "Now"), which records an
 * observation rather than an estimate: the truck is at the gate, and the time
 * it got there is a fact. It used to round to the nearest 5 like the estimate
 * options below, which was wrong in both directions — it discarded a real
 * measurement, and rounding to the *nearest* step meant 10:48 recorded an
 * arrival of 10:50, stamping a truck already on site with a time two minutes
 * in the future.
 *
 * This is the one action that can land on a different day than the delivery
 * started on: a truck showing up early is genuinely arriving *today*.
 */
export function nowSchedule(clock: Date = new Date()): string {
  return `${dayKeyOf(clock)}T${minutesToClock(clock.getHours() * 60 + clock.getMinutes())}`
}

/**
 * Now, rounded to the nearest `step` minutes — the base the arrival
 * *estimates* count from ("expected in 30").
 *
 * Coarse on purpose, and only here: an estimate reads as one at 11:00 where
 * 11:02 pretends to a precision nobody has. The distinction from nowSchedule
 * above is exactly the one the popover's own labels already make, between
 * "Expected in" and "Just arrived".
 */
export function nowScheduleRounded(
  step: number,
  clock: Date = new Date(),
): string {
  const minutes = clock.getHours() * 60 + clock.getMinutes()
  const rounded = Math.min(
    Math.round(minutes / step) * step,
    LAST_MINUTE_OF_DAY,
  )
  return `${dayKeyOf(clock)}T${minutesToClock(rounded)}`
}

/** `YYYY-MM-DD` for a date `days` from the given one, in local time. */
export function dayKeyOffset(from: Date, days: number): string {
  return dayKeyOf(
    new Date(from.getFullYear(), from.getMonth(), from.getDate() + days),
  )
}

/**
 * The narrowest board view (today/week/month) that shows a given day, given
 * today is a subset of week is a subset of month (see the board's viewCounts
 * comment). Callers with a specific row to land on — Show me, a paperwork
 * match — use this instead of a fixed widen target, so switching view never
 * hides the very row it was meant to reveal.
 *
 * `dayKey` must be `>= todayKey`; the board's Previous day(s) section has its
 * own handling (it shows in every view) and isn't a view-switch decision.
 */
export function viewForDayKey(
  dayKey: string,
  todayKey: string,
  weekEndKey: string,
): 'today' | 'week' | 'month' {
  if (dayKey === todayKey) return 'today'
  if (dayKey <= weekEndKey) return 'week'
  return 'month'
}

/**
 * Grace period before a passed ETA counts as late.
 *
 * Provisional: currently immediate — "late if the time is passed the ETA
 * time". The business may want breathing room; changing it is this one
 * number.
 */
export const LATE_GRACE_MINUTES = 0

/** Local ISO `YYYY-MM-DDTHH:MM` for a Date — comparable against scheduledFor. */
function toLocalIso(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${dayKeyOf(date)}T${hours}:${minutes}`
}

/**
 * The working ETA has passed (plus grace) and the truck is still expected.
 * The one attention flag: a nudge to check up on the delivery if you can —
 * once it is on site it is not late, it is just here. Stage is deliberately
 * not consulted here (pure time arithmetic); the caller applies it to ETA
 * rows only.
 */
export function isLate(
  scheduledFor: string,
  now: Date,
  graceMinutes: number = LATE_GRACE_MINUTES,
): boolean {
  if (!isValidSchedule(scheduledFor)) return false
  const cutoff = new Date(now.getTime() - graceMinutes * 60_000)
  return scheduledFor < toLocalIso(cutoff)
}

/**
 * Ascending by schedule. Unparseable values sort last — a placeholder for
 * deliveries with no firm ETA, which the board does not model yet.
 */
export function byScheduledTime<T extends { scheduledFor: string }>(
  a: T,
  b: T,
): number {
  const left = isValidSchedule(a.scheduledFor)
  const right = isValidSchedule(b.scheduledFor)
  if (!left) return right ? 1 : 0
  if (!right) return -1
  return a.scheduledFor.localeCompare(b.scheduledFor)
}

/**
 * Day-section heading. Near days lead with a relative word because that is
 * how the yard talks about them, but the actual date stays on the line —
 * "Today" alone is useless to someone scrolling a week ahead.
 */
export function formatDayLabel(
  dayKey: string,
  today: Date = new Date(),
): string {
  // Sliced rather than split: the format is fixed width, and indexing a
  // split() result is `number | undefined` under noUncheckedIndexedAccess.
  const date = new Date(
    Number(dayKey.slice(0, 4)),
    Number(dayKey.slice(5, 7)) - 1,
    Number(dayKey.slice(8, 10)),
  )

  const absolute = date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  const tomorrow = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 1,
  )

  if (dayKey === dayKeyOf(today)) return `Today · ${absolute}`
  if (dayKey === dayKeyOf(tomorrow)) return `Tomorrow · ${absolute}`
  return absolute
}

export type DayGroup<T> = {
  dayKey: string
  label: string
  deliveries: T[]
}

/** Section key for everything scheduled before today. */
export const PREVIOUS_DAYS_KEY = 'previous'

/**
 * Groups a flat list into day sections, sorted. Mirrors `screensByGroup()` in
 * configs/screens.tsx so the two grouping helpers read the same way.
 *
 * Days before today collapse into a single leading "Previous day(s)" section
 * rather than one header per stale date: what matters about those rows is
 * that they are outstanding, not which day they slipped from — the tile's
 * own rail still shows each one's time. Oldest first inside it, keeping the
 * whole board chronological.
 */
export function groupByDay<T extends { scheduledFor: string }>(
  items: T[],
  today: Date = new Date(),
): DayGroup<T>[] {
  const todayKey = dayKeyOf(today)
  const groups: DayGroup<T>[] = []

  for (const item of [...items].sort(byScheduledTime)) {
    const itemKey = toDayKey(item.scheduledFor)
    const isPrevious = isValidSchedule(item.scheduledFor) && itemKey < todayKey

    const dayKey = isPrevious ? PREVIOUS_DAYS_KEY : itemKey
    let group = groups.find((candidate) => candidate.dayKey === dayKey)
    if (!group) {
      group = {
        dayKey,
        label: isPrevious ? 'Previous day(s)' : formatDayLabel(itemKey, today),
        deliveries: [],
      }
      groups.push(group)
    }
    group.deliveries.push(item)
  }

  return groups
}
